import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Trash2, Route, Camera, X } from "lucide-react";
import { StageTracker, PROJECT_STAGES } from "@/components/StageTracker";

// Staff-side control: click a stage to move the customer-visible tracker; post progress notes.
export const ProjectStageCard = ({ project, onProjectChange }) => {
  const [updates, setUpdates] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);
  const [notify, setNotify] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of Array.from(files).slice(0, 6)) {
        const fd = new FormData(); fd.append("file", f); fd.append("project_id", project.project_id); fd.append("tag", "progress");
        const r = await axios.post(`${API}/photos/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.photo_id);
      }
      setPhotos(p => [...p, ...ids]);
    } catch (e) { toast.error(e.response?.data?.detail || "Photo upload failed"); }
    finally { setUploading(false); }
  };

  const load = () => axios.get(`${API}/projects/${project.project_id}/updates`).then(r => setUpdates(r.data)).catch(() => {});
  useEffect(() => { if (project?.project_id) load(); }, [project?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const stage = project.stage || "design";

  const commitStage = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/projects/${project.project_id}/stage`, { stage: pendingStage, note, notify, photo_ids: photos });
      onProjectChange?.({ ...project, stage: r.data.stage });
      const n = r.data.notify;
      toast.success(`Moved to ${PROJECT_STAGES.find(s => s.key === pendingStage)?.label}${n && (n.sms_sent || n.email_sent) ? " · customer notified" : ""}`);
      setPendingStage(null); setNote(""); setPhotos([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update stage"); }
    finally { setBusy(false); }
  };

  const postNote = async () => {
    if (!note.trim() && !photos.length) return;
    setBusy(true);
    try { await axios.post(`${API}/projects/${project.project_id}/updates`, { note, photo_ids: photos }); setNote(""); setPhotos([]); load(); toast.success("Update posted to customer portal"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to post update"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/projects/updates/${id}`); load(); }
    catch { toast.error("Failed to remove"); }
  };

  return (
    <Card data-testid="project-stage-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Route className="w-4 h-4 text-indigo-600" /> Customer Tracker</CardTitle>
        <CardDescription>Click a stage to move the project. Customers see this live in their portal, with your notes.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <StageTracker stage={pendingStage || stage} completed={project.status === "completed"} onSelect={k => setPendingStage(k === stage ? null : k)} compact />
        {pendingStage && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 text-sm flex flex-wrap items-center gap-2" data-testid="stage-confirm">
            Move to <b>{PROJECT_STAGES.find(s => s.key === pendingStage)?.label}</b>?
            <label className="flex items-center gap-1 text-xs text-slate-600 ml-1"><input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} data-testid="stage-notify-checkbox" /> Text customer</label>
            <Button size="sm" onClick={commitStage} disabled={busy} data-testid="stage-confirm-btn">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Confirm</Button>
            <Button size="sm" variant="ghost" onClick={() => setPendingStage(null)}>Cancel</Button>
          </div>
        )}
        <div className="flex gap-2">
          <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder={pendingStage ? "Optional note to include with the stage change…" : "Post a progress update for the customer (e.g. “Doors are in the paint booth, on track for the 21st”)"} data-testid="stage-note" />
          {!pendingStage && <Button size="icon" onClick={postNote} disabled={busy || (!note.trim() && !photos.length)} data-testid="post-update-btn"><Send className="w-4 h-4" /></Button>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-indigo-600 hover:underline cursor-pointer flex items-center gap-1" data-testid="attach-photo-label">
            <Camera className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Attach progress photo(s)"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={e => { uploadPhotos(e.target.files); e.target.value = ""; }} data-testid="attach-photo-input" />
          </label>
          {photos.map(id => (
            <span key={id} className="relative">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${id}`} alt="" className="w-12 h-12 object-cover rounded border" data-testid={`pending-photo-${id}`} />
              <button type="button" onClick={() => setPhotos(photos.filter(x => x !== id))} className="absolute -top-1 -right-1 bg-white rounded-full border text-slate-500 hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        {updates.length > 0 && (
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {updates.map(u => (
              <div key={u.update_id} className="flex items-start justify-between gap-2 text-sm border-l-2 border-indigo-200 pl-3" data-testid={`update-${u.update_id}`}>
                <div><p className="text-slate-800">{u.note}</p>
                  {u.photo_ids?.length > 0 && <div className="flex gap-1 mt-1">{u.photo_ids.map(id => <img key={id} src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${id}`} alt="" className="w-14 h-14 object-cover rounded border" />)}</div>}
                  <p className="text-xs text-slate-400">{u.stage_label} · {new Date(u.created_at).toLocaleString()}{u.created_by_name && ` · ${u.created_by_name}`}</p></div>
                <button onClick={() => remove(u.update_id)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
